import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildExternalMonitoringPeriod,
  formatExternalMonitoringPeriodLabelRu,
  type ExternalMonitoringPeriodBounds,
} from '../../../../../helpers/external-monitoring-period';
import {
  fetchAggregationAvailableResources,
  fetchAggregationResultsLanguagesRows,
  fetchExternalResourceCategories,
  fetchExternalResourceLanguages,
  type ExternalResourceCategory,
  type ExternalResourceLanguage,
} from '../../../../../store/api/monitoring-api';
import { displayLabelForStoredCommentLanguageCode } from '../../../../../store/api/external-reviews-items-api';
import { aggregateSentimentDynamicsBucketsFromSentimentRows } from './charts/build-sentiment-dynamics-from-rows';
import { useSentimentDynamicsLine } from './charts/use-sentiment-dynamics-line';
import { useHeatmapCategorySentiment } from './charts/use-heatmap-category-sentiment.tsx';
import { useAspectsSentimentMasterDetail } from './charts/use-aspects-sentiment-master-detail';
import { useCommentsByLanguagePie } from './charts/use-comments-by-language-pie';
import { useNegativeByLanguageBar } from './charts/use-negative-by-language-bar';
import { useAspectsDynamicsByCategoryLine } from './charts/use-aspects-dynamics-by-category-line';
import { ExternalMonitoringFiltersState } from './dashboard-external-monitoring-filters';
import {
  applyCategoryFilterToDashboardModel,
  // applyAspectFilterToDashboardModel,
  buildPyramidChartApiFromRowsEnriched,
  loadExternalMonitoringDashboardModel,
  type ExternalMonitoringDashboardModel,
} from './external-monitoring-dashboard-model';
import {
  type ExternalMonitoringLanguageAggregation,
  type ExternalMonitoringLanguageAggregationItem,
} from './language-aggregation';
import s from './dashboard-external-monitoring.module.css';

export type SourceOption = { id: string; value: string };

const ALL_SOURCES: SourceOption = { id: 'all', value: 'Все источники' };

export type UseDashboardExternalMonitoringParams = {
  filters: ExternalMonitoringFiltersState;
};

export const useDashboardExternalMonitoring = (
  params?: UseDashboardExternalMonitoringParams,
) => {
  const filters = params?.filters;
  const sourceIdFilter = filters?.sourceId;
  const groupingIntervalFilter = filters?.groupingInterval ?? 'day';
  const categoryIdFilter = filters?.categoryId ?? 'all';
  // const aspectIdFilter = filters?.aspectId ?? 'all';
  const periodFallback = useMemo(() => buildExternalMonitoringPeriod('1'), []);
  const periodBeginSec = filters?.periodBeginSec ?? periodFallback.begin;
  const periodEndSec = filters?.periodEndSec ?? periodFallback.end;
  const [availableResources, setAvailableResources] = useState<{ id: number; name: string }[]>([]);
  const [externalResourceLanguages, setExternalResourceLanguages] = useState<ExternalResourceLanguage[]>(
    [],
  );
  const [externalResourceCategories, setExternalResourceCategories] = useState<ExternalResourceCategory[]>(
    [],
  );
  const [dashboardModel, setDashboardModel] = useState<ExternalMonitoringDashboardModel | null>(null);
  const [languageAggregation, setLanguageAggregation] =
    useState<ExternalMonitoringLanguageAggregation | null>(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  /** Инкремент на каждый новый запрос — отбрасываем устаревший ответ при быстрой смене периода/источника */
  const dashboardLoadSeqRef = useRef(0);

  const loadAvailable = useCallback(() => {
    fetchAggregationAvailableResources()
      .then(setAvailableResources)
      .catch(() => setAvailableResources([]));
  }, []);

  useEffect(() => {
    loadAvailable();
  }, [loadAvailable]);

  useEffect(() => {
    fetchExternalResourceLanguages()
      .then(setExternalResourceLanguages)
      .catch(() => setExternalResourceLanguages([]));
  }, []);

  useEffect(() => {
    fetchExternalResourceCategories()
      .then(setExternalResourceCategories)
      .catch(() => setExternalResourceCategories([]));
  }, []);

  const languagesById = useMemo(() => {
    return new Map(externalResourceLanguages.map((l) => [l.id, l.name]));
  }, [externalResourceLanguages]);

  // Смена источника или периода перезапускает загрузку (begin/end пересчитываются в loadExternalMonitoringDashboardModel)
  useEffect(() => {
    if (!sourceIdFilter) {
      setDashboardModel(null);
      setIsDashboardLoading(false);
      return;
    }

    if (availableResources.length === 0) {
      setDashboardModel(null);
      setIsDashboardLoading(false);
      return;
    }

    let resourcesParam: string;
    if (sourceIdFilter === 'all') {
      resourcesParam = availableResources.map((r) => r.id).join(',');
    } else {
      const resourceId = Number(sourceIdFilter);
      if (!Number.isFinite(resourceId)) {
        setDashboardModel(null);
        setIsDashboardLoading(false);
        return;
      }
      const resourceFromAvailable = availableResources.find((r) => r.id === resourceId);
      if (!resourceFromAvailable) {
        setDashboardModel(null);
        setIsDashboardLoading(false);
        return;
      }
      resourcesParam = sourceIdFilter;
    }

    const loadSeq = ++dashboardLoadSeqRef.current;
    let cancelled = false;

    setIsDashboardLoading(true);
    setDashboardModel(null);
    setLanguageAggregation(null);

    Promise.all([
      loadExternalMonitoringDashboardModel({
        resources: resourcesParam,
        periodBegin: periodBeginSec,
        periodEnd: periodEndSec,
        groupingInterval: groupingIntervalFilter,
      }),
      fetchAggregationResultsLanguagesRows(resourcesParam, {
        begin: periodBeginSec,
        end: periodEndSec,
        grouping_interval: groupingIntervalFilter,
      }),
    ])
      .then(([model, languagesRows]) => {
        if (cancelled || loadSeq !== dashboardLoadSeqRef.current) return;
        setDashboardModel(model);
        const totalsByLanguageId = new Map<number, ExternalMonitoringLanguageAggregationItem>();
        for (const providerRow of languagesRows) {
          for (const item of providerRow.languages ?? []) {
            const id = Number(item.id);
            if (!Number.isFinite(id)) continue;

            const existing = totalsByLanguageId.get(id) ?? {
              languageId: id,
              languageCode: '',
              languageLabel: `ID ${id}`,
              commentsCount: 0,
              positiveCount: 0,
              neutralCount: 0,
              negativeCount: 0,
            };
            const languageCode = (languagesById.get(id) ?? '').trim().toLowerCase();
            if (languageCode) {
              existing.languageCode = languageCode;
              existing.languageLabel = displayLabelForStoredCommentLanguageCode(languageCode);
            }

            existing.commentsCount += Number(item.comments_count ?? 0);
            existing.positiveCount += Number(item.positive_count ?? 0);
            existing.neutralCount += Number(item.neutral_count ?? 0);
            existing.negativeCount += Number(item.negative_count ?? 0);
            totalsByLanguageId.set(id, existing);
          }
        }
        const items = Array.from(totalsByLanguageId.values())
          .filter((x) => x.commentsCount > 0 && x.languageCode !== 'junk')
          .sort((a, b) => b.commentsCount - a.commentsCount);
        setLanguageAggregation({ items });
      })
      .catch(() => {
        if (!cancelled && loadSeq === dashboardLoadSeqRef.current) {
          setDashboardModel(null);
          setLanguageAggregation(null);
        }
      })
      .finally(() => {
        if (!cancelled && loadSeq === dashboardLoadSeqRef.current) setIsDashboardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    sourceIdFilter,
    periodBeginSec,
    periodEndSec,
    groupingIntervalFilter,
    availableResources,
    languagesById,
  ]);

  const displayDashboardModel = useMemo(
    () => {
      if (dashboardModel == null) return null;
      const byCategory = applyCategoryFilterToDashboardModel(dashboardModel, categoryIdFilter);
      return byCategory;
      // return applyAspectFilterToDashboardModel(byCategory, aspectIdFilter);
    },
    [dashboardModel, categoryIdFilter /* , aspectIdFilter */],
  );

  const sourceOptions = useMemo<SourceOption[]>(() => {
    return [ALL_SOURCES, ...availableResources.map((r) => ({ id: String(r.id), value: r.name }))];
  }, [availableResources]);

  const periodLabelFromFilters = formatExternalMonitoringPeriodLabelRu(periodBeginSec, periodEndSec);

  const periodLabel =
    displayDashboardModel?.period.labelFromRows ??
    displayDashboardModel?.period.labelRu ??
    periodLabelFromFilters;

  const kpiPayload = displayDashboardModel?.kpiPayload ?? null;

  /** Позитив − негатив по бакетам — те же бакеты, что у «Динамика тональности» (type=sentiment). */
  const toneNetSparklineValues = useMemo(() => {
    const sentimentRows = displayDashboardModel?.sentimentRows;
    if (!sentimentRows?.length) return null;
    const { buckets } = aggregateSentimentDynamicsBucketsFromSentimentRows(
      sentimentRows,
      groupingIntervalFilter,
    );
    if (buckets.length === 0) return null;
    return buckets.map((t) => t.pos - t.neg);
  }, [displayDashboardModel?.sentimentRows, groupingIntervalFilter]);

  const periodBounds: ExternalMonitoringPeriodBounds = useMemo(() => {
    if (dashboardModel?.period != null) {
      return {
        begin: dashboardModel.period.begin,
        end: dashboardModel.period.end,
      };
    }
    return { begin: periodBeginSec, end: periodEndSec };
  }, [dashboardModel?.period?.begin, dashboardModel?.period?.end, periodBeginSec, periodEndSec]);

  const pyramidChartApi = useMemo(
    () =>
      displayDashboardModel?.rowsEnriched?.length
        ? buildPyramidChartApiFromRowsEnriched(displayDashboardModel.rowsEnriched)
        : undefined,
    [displayDashboardModel?.rowsEnriched],
  );

  const heatmapApi = displayDashboardModel?.heatmapPayload;

  const sentimentDynamicsChart = useSentimentDynamicsLine(
    periodBounds,
    displayDashboardModel
      ? {
          rows: displayDashboardModel.rows,
          sentimentRows: displayDashboardModel.sentimentRows,
          groupingInterval: groupingIntervalFilter,
        }
      : undefined,
  );

  return {
    chartData: [
      {
        ...sentimentDynamicsChart,
        hidden: categoryIdFilter !== 'all',
      },
      useAspectsDynamicsByCategoryLine(
        periodBounds,
        displayDashboardModel
          ? {
              rows: displayDashboardModel.rows,
              categoryNamesById: displayDashboardModel.categoryNamesById,
              groupingInterval: groupingIntervalFilter,
            }
          : undefined,
      ),
      useHeatmapCategorySentiment(filters?.sentimentKey, heatmapApi, periodBounds),
      ...useAspectsSentimentMasterDetail(pyramidChartApi, periodBounds),
      useCommentsByLanguagePie(periodBounds, languageAggregation),
      useNegativeByLanguageBar(periodBounds, languageAggregation),
    ].map((chart) => ({
      ...chart,
      tooltipPanelClassName: s.externalTooltipPanel,
    })),
    sourceOptions,
    /** Сырой список из GET aggregation/available — те же id, что уходят в results?resources= */
    availableResources,
    /** GET external-resource/languages */
    externalResourceLanguages,
    /** GET external-resource/categories — для селекта фильтра */
    externalResourceCategories,
    kpiPayload,
    toneNetSparklineValues,
    periodLabel,
    /** Данные дашборда с учётом фильтра категории (для KPI и графиков) */
    dashboardModel: displayDashboardModel,
    isDashboardLoading,
  };
};
